import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Send, Sparkles, MessageCircle, Trash2, ExternalLink, Crown, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

/**
 * ╔════════════════════════════════════════════════════════════════════════════╗
 * ║                    ASSISTANT V1.1 CANONIQUE — HEAREN                       ║
 * ║                                                                            ║
 * ║  Your personal calm companion.                                             ║
 * ║  Calm, premium, human, non-medical.                                        ║
 * ║  HARD PAYWALL for Free users (no access at all).                          ║
 * ║                                                                            ║
 * ║  DO NOT MODIFY WITHOUT EXPLICIT PRODUCT DECISION                          ║
 * ╚════════════════════════════════════════════════════════════════════════════╝
 */

const Assistant = ({ t = {} }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const lang = localStorage.getItem('hearen_lang') || 'fr';
  const isFreeUser = !user?.isPremium && user?.subscription !== 'premium';
  
  // ALL HOOKS MUST BE DECLARED BEFORE ANY CONDITIONAL RETURNS
  // State
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [error, setError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  
  // V1 WORDING - Welcome message
  const getWelcomeMessage = () => {
    if (lang === 'en') {
      return `Hello 👋
I'm Hearen, your calm companion.

I can help you understand your tinnitus, choose the right sounds, or simply feel calmer right now.

How would you like to begin?`;
    }
    return `Bonjour 👋
Je suis Hearen, ton compagnon de calme.

Je peux t'aider à mieux comprendre tes acouphènes, à choisir les sons qui te conviennent, ou simplement à t'apaiser maintenant.

Comment souhaites-tu commencer ?`;
  };
  
  // V1 WORDING - Quick actions
  const quickActions = lang === 'en' 
    ? [
        "Understand my tinnitus",
        "Calm my breathing",
        "Find the right sound",
        "I need calm now"
      ]
    : [
        "Comprendre mes acouphènes",
        "Apaiser ma respiration",
        "Trouver le bon son",
        "J'ai besoin de calme maintenant"
      ];
  
  // V1.1 WORDING - Texts (Premium users only - no paywall texts needed)
  const texts = {
    title: lang === 'en' ? 'Hearen Assistant' : 'Assistant Hearen',
    subtitle: lang === 'en' ? 'Your personal calm companion' : 'Ton compagnon de calme personnel',
    disclaimer: lang === 'en' 
      ? "💡 I'm here to support your well-being and calm. I don't replace medical advice."
      : "💡 Je suis là pour soutenir ton bien-être et ton apaisement. Je ne remplace pas un avis médical.",
    privacyTitle: lang === 'en' ? 'Privacy' : 'Confidentialité',
    privacyText: lang === 'en' 
      ? 'Your conversations with Hearen stay private. You can delete them at any time.'
      : 'Tes échanges avec Hearen restent privés. Tu peux les supprimer à tout moment.',
    placeholder: lang === 'en' ? 'Type your message...' : 'Écris ton message...',
    typing: lang === 'en' ? 'Thinking...' : 'Réflexion...',
    deleteBtn: lang === 'en' ? 'New chat' : 'Nouvelle conversation',
    deleteConfirmTitle: lang === 'en' ? 'Start a new conversation?' : 'Démarrer une nouvelle conversation ?',
    deleteConfirmMessage: lang === 'en' 
      ? 'This will delete the current conversation. This action cannot be undone.'
      : 'Cela supprimera la conversation actuelle. Cette action est irréversible.',
    cancel: lang === 'en' ? 'Cancel' : 'Annuler',
    confirm: lang === 'en' ? 'Confirm' : 'Confirmer',
  };
  
  // Scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  // Load welcome message (only for Premium users)
  useEffect(() => {
    if (!isFreeUser && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: getWelcomeMessage(),
        timestamp: new Date().toISOString()
      }]);
    }
  }, [isFreeUser]);
  
  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [input]);
  
  const handleQuickAction = async (action) => {
    // Directly send the message without requiring a click on send button
    if (isLoading) return;
    
    const userMessage = {
      role: 'user',
      content: action,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInput(''); // Clear any existing input
    setIsLoading(true);
    setError('');
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/assistant/chat`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          body: JSON.stringify({
            message: action,
            conversation_id: conversationId
          })
        }
      );
      
      if (response.status === 403) {
        setError(lang === 'en'
          ? 'The Assistant is reserved for Premium members.'
          : "L'Assistant est réservé aux membres Premium.");
        return;
      }
      if (!response.ok) {
        setError(lang === 'en'
          ? 'The service is temporarily unavailable. Please try again later.'
          : 'Le service est temporairement indisponible. Réessaie plus tard.');
        return;
      }
      
      const data = await response.json();
      
      // Save conversation ID for future messages
      if (data.conversation_id) {
        setConversationId(data.conversation_id);
      }
      
      const assistantMessage = {
        role: 'assistant',
        content: data.response,
        timestamp: new Date().toISOString()
      };
      
      setMessages(prev => [...prev, assistantMessage]);
    } catch (err) {
      console.error('Error sending message:', err);
      setError(lang === 'en' 
        ? 'Unable to get a response. Please try again.'
        : 'Impossible d\'obtenir une réponse. Peux-tu réessayer ?'
      );
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSend = async () => {
    if (!input.trim() || isLoading) return;
    
    const userMessage = {
      role: 'user',
      content: input,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setError('');
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/assistant/chat`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          body: JSON.stringify({
            message: userMessage.content,
            conversation_id: conversationId
          })
        }
      );
      
      if (response.status === 403) {
        setError(lang === 'en'
          ? 'The Assistant is reserved for Premium members.'
          : "L'Assistant est réservé aux membres Premium.");
        return;
      }
      if (!response.ok) {
        setError(lang === 'en'
          ? 'The service is temporarily unavailable. Please try again later.'
          : 'Le service est temporairement indisponible. Réessaie plus tard.');
        return;
      }
      
      const data = await response.json();
      
      if (!conversationId) {
        setConversationId(data.conversation_id);
      }
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.response,
        timestamp: data.timestamp
      }]);
      
    } catch (error) {
      console.error('Error sending message:', error);
      setError(lang === 'en' ? "Sorry, I encountered an error. Can you try again?" : "Désolé, j'ai rencontré une erreur. Peux-tu réessayer ?");
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  
  const handleDeleteConversation = async () => {
    if (conversationId) {
      setIsDeleting(true);
      try {
        await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/assistant/conversations/${conversationId}`,
          {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
          }
        );
      } catch (error) {
        console.error('Error deleting conversation:', error);
      } finally {
        setIsDeleting(false);
      }
    }
    
    // Reset
    setConversationId(null);
    setMessages([{
      role: 'assistant',
      content: getWelcomeMessage(),
      timestamp: new Date().toISOString()
    }]);
    setShowDeleteConfirm(false);
    setError('');
  };
  
  // Render message with clickable links
  const renderMessageContent = (content) => {
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = linkRegex.exec(content)) !== null) {
      // Capture values in local constants to avoid closure issues
      const linkText = match[1];
      const linkUrl = match[2];
      const matchIndex = match.index;
      const matchLength = match[0].length;
      
      if (matchIndex > lastIndex) {
        parts.push(<span key={`text-${lastIndex}`}>{content.substring(lastIndex, matchIndex)}</span>);
      }
      parts.push(
        <button
          key={`link-${matchIndex}`}
          onClick={() => navigate(linkUrl)}
          className="inline-flex items-center gap-1 px-3 py-1.5 mx-1 bg-violet-500/30 hover:bg-violet-500/50 rounded-lg transition-colors text-sm font-medium"
        >
          {linkText}
          <ExternalLink className="w-3 h-3" />
        </button>
      );
      lastIndex = matchIndex + matchLength;
    }
    
    if (lastIndex < content.length) {
      parts.push(<span key={`text-${lastIndex}`}>{content.substring(lastIndex)}</span>);
    }
    
    return parts.length > 0 ? parts : content;
  };
  
  // ⭐ V1.1 HARD PAYWALL: If Free user, show paywall immediately - NO ACCESS
  if (isFreeUser) {
    return (
      <div 
        className="min-h-screen pb-40 relative flex items-center justify-center"
        style={{ background: '#0a0a12' }}
      >
        {/* Subtle cosmic background */}
        <div 
          className="fixed inset-0 pointer-events-none"
          style={{
            background: `
              radial-gradient(ellipse at 30% 20%, rgba(88, 28, 135, 0.12) 0%, transparent 50%),
              radial-gradient(ellipse at 70% 80%, rgba(59, 72, 238, 0.08) 0%, transparent 50%)
            `,
          }}
        />
        
        {/* Premium-Only Paywall */}
        <div className="relative z-10 max-w-sm w-full mx-4 bg-[#1a1a24] border border-white/10 rounded-2xl p-6">
          <div className="text-center mb-6">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-violet-500/30 to-purple-600/30 flex items-center justify-center">
              <Crown className="w-8 h-8 text-violet-400" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">
              {lang === 'en' ? 'Hearen Premium' : 'Hearen Premium'}
            </h2>
            <p className="text-sm text-white/60 leading-relaxed mb-2">
              {lang === 'en' 
                ? 'The Hearen Assistant is part of the Hearen Premium experience.'
                : "L’Assistant Hearen fait partie de l’expérience Hearen Premium."}
            </p>
            <p className="text-xs text-white/40">
              {lang === 'en'
                ? 'Get personalized guidance, sound recommendations, and calming support.'
                : 'Obtenez des conseils personnalisés, des recommandations de sons et un soutien apaisant.'}
            </p>
          </div>
          
          <div className="space-y-3">
            <button
              onClick={() => navigate('/premium')}
              className="w-full py-3 px-4 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white font-medium rounded-xl transition-all flex items-center justify-center gap-2"
            >
              <Crown className="w-4 h-4" />
              {lang === 'en' ? 'Unlock Premium' : 'Débloquer Premium'}
            </button>
            
            <button
              onClick={() => navigate('/home')}
              className="w-full py-2 text-sm text-white/50 hover:text-white/70 transition-colors"
            >
              {lang === 'en' ? 'Back to Home' : "Retour à l'accueil"}
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // ============================================
  // PREMIUM USER UI - Full Assistant experience
  // ============================================
  return (
    <div 
      className="min-h-screen pb-40 relative overflow-x-hidden"
      style={{ background: '#0a0a12' }}
    >
      {/* Subtle cosmic background */}
      <div 
        className="fixed inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse at 30% 20%, rgba(88, 28, 135, 0.12) 0%, transparent 50%),
            radial-gradient(ellipse at 70% 80%, rgba(59, 72, 238, 0.08) 0%, transparent 50%)
          `,
        }}
      />
      
      {/* Content */}
      <div className="relative z-10 max-w-lg mx-auto px-4 pt-16">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500/30 to-purple-600/30 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-violet-300" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-white">{texts.title}</h1>
              <p className="text-xs text-white/50">{texts.subtitle}</p>
            </div>
          </div>
          
          {/* New chat button */}
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition-colors text-xs text-white/70"
          >
            {texts.deleteBtn}
          </button>
        </div>
        
        {/* Disclaimer - V1 wording */}
        <div className="mb-4 p-3 rounded-xl bg-white/5 border border-white/10">
          <p className="text-xs text-white/60 leading-relaxed">{texts.disclaimer}</p>
        </div>
        
        {/* Quick Actions - Show only at start */}
        {messages.length <= 1 && (
          <div className="mb-4 grid grid-cols-2 gap-2">
            {quickActions.map((action, index) => (
              <button
                key={index}
                onClick={() => handleQuickAction(action)}
                className="p-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-left text-sm text-white/80 transition-all"
              >
                {action}
              </button>
            ))}
          </div>
        )}
        
        {/* Messages */}
        <div className="space-y-3 mb-4">
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] p-4 rounded-2xl ${
                  message.role === 'user'
                    ? 'bg-violet-600 text-white rounded-br-sm'
                    : 'bg-white/10 text-white rounded-bl-sm'
                }`}
              >
                {message.role === 'assistant' && (
                  <div className="flex items-center gap-2 mb-2">
                    <MessageCircle className="w-3 h-3 text-violet-400" />
                    <span className="text-xs text-violet-400 font-medium">Hearen</span>
                  </div>
                )}
                <div className="whitespace-pre-wrap text-sm leading-relaxed">
                  {renderMessageContent(message.content)}
                </div>
              </div>
            </div>
          ))}
          
          {/* Loading indicator */}
          {isLoading && (
            <div className="flex justify-start">
              <div className="max-w-[85%] p-4 rounded-2xl bg-white/10 text-white rounded-bl-sm">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-violet-400">{texts.typing}</span>
                  <div className="flex gap-1">
                    <div className="w-1.5 h-1.5 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-1.5 h-1.5 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-1.5 h-1.5 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {error && (
            <div className="p-3 rounded-xl bg-red-500/20 border border-red-500/30">
              <p className="text-sm text-red-300">{error}</p>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </div>
      
      {/* Fixed Input Area */}
      <div 
        className="fixed bottom-20 left-0 right-0 bg-[#0a0a12]/95 backdrop-blur-md border-t border-white/10 p-4"
        style={{
          paddingLeft: 'max(16px, env(safe-area-inset-left))',
          paddingRight: 'max(16px, env(safe-area-inset-right))',
        }}
      >
        <div className="max-w-lg mx-auto w-full">
          <div className="flex gap-2 items-end">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={texts.placeholder}
              disabled={isLoading}
              rows={1}
              className="flex-1 min-w-0 p-3 bg-white/5 border border-white/10 rounded-xl text-white text-sm placeholder-white/30 resize-none focus:outline-none focus:ring-1 focus:ring-violet-500/50 disabled:opacity-50 max-h-24 overflow-y-auto"
              style={{ minHeight: '44px' }}
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || isLoading}
              className="flex-shrink-0 p-3 bg-violet-600 hover:bg-violet-700 disabled:bg-white/10 disabled:cursor-not-allowed rounded-xl transition-colors"
            >
              <Send className="w-4 h-4 text-white" />
            </button>
          </div>
          
          {/* Privacy Footer - V1 wording */}
          <div className="mt-2 pt-2 border-t border-white/5">
            <p className="text-[10px] text-white/30">
              <span className="font-medium">{texts.privacyTitle}</span> · {texts.privacyText}
            </p>
          </div>
        </div>
      </div>
      
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="max-w-sm w-full bg-[#1a1a24] border border-white/10 rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-white mb-2">{texts.deleteConfirmTitle}</h3>
            <p className="text-sm text-white/60 mb-6">{texts.deleteConfirmMessage}</p>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                className="flex-1 py-2 px-4 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-colors disabled:opacity-50"
              >
                {texts.cancel}
              </button>
              <button
                onClick={handleDeleteConversation}
                disabled={isDeleting}
                className="flex-1 py-2 px-4 bg-violet-600 hover:bg-violet-700 text-white rounded-xl transition-colors disabled:opacity-50"
              >
                {isDeleting ? '...' : texts.confirm}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Assistant;
